import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmptyState, FormDialog, SectionCard, StatusChip, TableSkeleton } from '../../components/ui';
import { SegmentedControl } from '../../components/mapping/v2/v2Ui';
import { useToast } from '../../contexts/ToastContext';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { tokens } from '../../theme/tokens';
import { asDate, isRecent, privacyDue, systemJson } from './api';

type PrivacyType = 'access' | 'delete' | 'correct' | 'portability' | 'opt_out_sale_share' | 'limit_sensitive_use';
type PrivacyStatus = 'pending' | 'verified' | 'in_progress' | 'completed' | 'denied' | 'cancelled';

interface PrivacyRequest {
  id: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  type: PrivacyType;
  status: PrivacyStatus;
  requestedAt?: unknown;
  updatedAt?: unknown;
  denialReason?: string;
}

interface SlackRequest {
  userId: string;
  requestId: string;
  name: string;
  email: string;
  organizationName: string | null;
  note?: string;
  status: 'open' | 'invited' | 'dismissed';
  createdAt?: unknown;
  resolvedAt?: unknown;
  resolvedBy?: string;
}

type Queue = 'open' | 'closed';

const closedDate = (value: unknown): string =>
  asDate(value)?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) || '—';
type KindFilter = 'all' | 'privacy' | 'slack';

const OPEN_PRIVACY = new Set(['pending', 'verified', 'in_progress']);
const FULFILL_TYPES = new Set(['delete', 'correct']);

interface Row {
  key: string;
  kind: 'privacy' | 'slack';
  who: string;
  email: string;
  org: string;
  opened: unknown;
  dueDays: number | null;
  status: string;
  note?: string;
  closedNote?: string;
  privacy?: PrivacyRequest;
  slack?: SlackRequest;
}

export const RequestsTab: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState<Queue>('open');
  const [kind, setKind] = useState<KindFilter>('all');
  const [dialog, setDialog] = useState<{ mode: 'fulfill' | 'deny'; request: PrivacyRequest } | null>(null);
  const [notes, setNotes] = useState('');

  const privacy = useQuery({
    queryKey: ['admin-privacy-requests', 'all'],
    queryFn: () => systemJson<PrivacyRequest[]>('/admin/privacy-requests?limit=200'),
  });
  const slackOpen = useQuery({
    queryKey: ['admin-partner-slack', 'open'],
    queryFn: () => systemJson<{ requests: SlackRequest[] }>('/admin/partner-slack/requests?status=open'),
  });
  const slackInvited = useQuery({
    queryKey: ['admin-partner-slack', 'invited'],
    queryFn: () => systemJson<{ requests: SlackRequest[] }>('/admin/partner-slack/requests?status=invited'),
  });
  const slackDismissed = useQuery({
    queryKey: ['admin-partner-slack', 'dismissed'],
    queryFn: () => systemJson<{ requests: SlackRequest[] }>('/admin/partner-slack/requests?status=dismissed'),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-privacy-requests'] });
    queryClient.invalidateQueries({ queryKey: ['admin-partner-slack'] });
    queryClient.invalidateQueries({ queryKey: ['admin-system-requests-badge'] });
  };

  const statusMutation = useMutation({
    mutationFn: (body: { id: string; status: string; denialReason?: string }) =>
      systemJson(`/admin/privacy-requests/${body.id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: body.status, denialReason: body.denialReason }),
      }),
    onSuccess: () => {
      showToast(t('admin.systemV2.requests.updated'), 'success');
      setDialog(null);
      refresh();
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });
  const fulfillMutation = useMutation({
    mutationFn: (body: { id: string; notes?: string }) =>
      systemJson(`/admin/privacy-requests/${body.id}/fulfill`, {
        method: 'POST',
        body: JSON.stringify({ notes: body.notes || undefined }),
      }),
    onSuccess: () => {
      showToast(t('admin.systemV2.requests.updated'), 'success');
      setDialog(null);
      refresh();
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });
  const slackMutation = useMutation({
    mutationFn: (body: { userId: string; requestId: string; status: 'invited' | 'dismissed' }) =>
      systemJson(`/admin/partner-slack/requests/${encodeURIComponent(body.userId)}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ requestId: body.requestId, status: body.status }),
      }),
    onSuccess: () => {
      showToast(t('admin.systemV2.requests.updated'), 'success');
      refresh();
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const rows = useMemo(() => {
    const list: Row[] = [];
    for (const request of privacy.data || []) {
      const due = privacyDue(request.requestedAt);
      const open = OPEN_PRIVACY.has(request.status);
      const when = request.updatedAt || request.requestedAt;
      if (!open && !isRecent(when)) continue;
      list.push({
        key: `p-${request.id}`,
        kind: 'privacy',
        who: request.userName || request.userEmail || request.userId,
        email: request.userEmail || '',
        org: '—',
        opened: request.requestedAt,
        dueDays: open ? due?.daysLeft ?? null : null,
        status: request.status,
        privacy: request,
        closedNote: !open && asDate(request.updatedAt)
          ? t('admin.systemV2.requests.closedOn', { status: t(`admin.systemV2.requests.status.${request.status}`, { defaultValue: request.status }), when: closedDate(request.updatedAt) })
          : undefined,
      });
    }
    const slack = [...(slackOpen.data?.requests || []), ...(slackInvited.data?.requests || []), ...(slackDismissed.data?.requests || [])];
    for (const request of slack) {
      const open = request.status === 'open';
      const when = request.resolvedAt || request.createdAt;
      if (!open && !isRecent(when)) continue;
      list.push({
        key: `s-${request.userId}`,
        kind: 'slack',
        who: request.name || request.email,
        email: request.email,
        org: request.organizationName || '—',
        opened: request.createdAt,
        dueDays: null,
        status: request.status,
        note: request.note,
        slack: request,
        closedNote: !open
          ? [
              t('admin.systemV2.requests.closedOn', { status: t(`admin.systemV2.requests.status.${request.status}`, { defaultValue: request.status }), when: closedDate(request.resolvedAt) }),
              request.resolvedBy,
            ].filter(Boolean).join(' · ')
          : undefined,
      });
    }
    return list.sort((a, b) => (asDate(b.opened)?.getTime() || 0) - (asDate(a.opened)?.getTime() || 0));
  }, [privacy.data, slackOpen.data, slackInvited.data, slackDismissed.data, t]);

  const openRows = rows.filter((row) => (row.kind === 'privacy' ? OPEN_PRIVACY.has(row.status) : row.status === 'open'));
  const closedRows = rows.filter((row) => !openRows.includes(row));
  const visible = (queue === 'open' ? openRows : closedRows).filter((row) => kind === 'all' || row.kind === kind);
  const loading = privacy.isLoading || slackOpen.isLoading;

  const kindLabel = (row: Row) =>
    row.kind === 'slack' ? t('admin.systemV2.requests.slackKind') : t(`admin.systemV2.requests.types.${row.privacy?.type || 'access'}`);
  const shortDate = (value: unknown) =>
    asDate(value)?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) || '—';
  const rowTone = (row: Row): 'caution' | 'info' | 'neutral' | 'problem' => {
    if (row.status === 'in_progress') return 'info';
    if (row.status === 'open' || row.status === 'pending' || row.status === 'verified') return 'caution';
    if (row.status === 'denied') return 'problem';
    return 'neutral';
  };
  const dueLine = (row: Row): { text: string; urgent: boolean } | null => {
    const open = row.kind === 'privacy' ? OPEN_PRIVACY.has(row.status) : row.status === 'open';
    if (!open) return null;
    if (row.kind === 'slack') return { text: t('admin.systemV2.requests.noDeadline'), urgent: false };
    if (row.dueDays === null) return null;
    if (row.dueDays < 0) return { text: t('admin.systemV2.requests.overdue', { count: -row.dueDays }), urgent: true };
    return { text: t('admin.systemV2.requests.dueIn', { count: row.dueDays }), urgent: row.dueDays <= 7 };
  };

  const cell = { px: 2, py: 1.5, borderBottom: `1px solid ${tokens.line.hairline}`, verticalAlign: 'middle' } as const;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <Typography sx={{ fontSize: 13, color: tokens.ink.muted, maxWidth: 820 }}>{t('admin.systemV2.requests.intro')}</Typography>
      <SectionCard dense>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: 2, py: 1.5, borderBottom: `1px solid ${tokens.line.hairline}` }}>
          <SegmentedControl
            kind="filter"
            aria-label={t('admin.systemV2.requests.queueLabel')}
            value={queue}
            onChange={setQueue}
            options={[
              { value: 'open', label: t('admin.systemV2.requests.queue.open'), count: openRows.length, countTone: openRows.length ? 'caution' : undefined },
              { value: 'closed', label: t('admin.systemV2.requests.queue.closed'), count: closedRows.length },
            ]}
          />
          <Box role="group" aria-label={t('admin.systemV2.requests.kindLabel')} sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {(['all', 'privacy', 'slack'] as const).map((value) => {
              const selected = kind === value;
              return (
                <Chip
                  key={value}
                  label={t(`admin.systemV2.requests.kind.${value}`)}
                  clickable
                  aria-pressed={selected}
                  onClick={() => setKind(value)}
                  variant={selected ? 'filled' : 'outlined'}
                  sx={selected ? { bgcolor: tokens.surface.header, color: tokens.ink.inverse, '&:hover': { bgcolor: tokens.ink.body } } : undefined}
                />
              );
            })}
          </Box>
        </Box>
        {loading ? (
          <TableSkeleton rows={4} columns={6} />
        ) : visible.length === 0 ? (
          <EmptyState
            title={t(`admin.systemV2.requests.empty.${queue}`)}
            description={queue === 'open' ? t('admin.systemV2.requests.emptyHint') : t('admin.systemV2.requests.closedWindow')}
          />
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', minWidth: 880, borderCollapse: 'collapse', fontSize: 13 }}>
              <Box component="thead">
                <Box component="tr">
                  {(['request', 'from', 'org', 'submitted', 'status', 'actions'] as const).map((column) => (
                    <Box
                      component="th"
                      key={column}
                      sx={{
                        px: 2,
                        py: 1.125,
                        borderBottom: `1px solid ${tokens.line.hairline}`,
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: tokens.ink.muted,
                        textAlign: column === 'actions' ? 'right' : 'left',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t(`admin.systemV2.requests.columns.${column}`)}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {visible.map((row) => {
                  const due = dueLine(row);
                  const privacyOpen = row.privacy && OPEN_PRIVACY.has(row.privacy.status);
                  const fulfills = FULFILL_TYPES.has(row.privacy?.type || '');
                  return (
                    <Box component="tr" key={row.key}>
                      <Box component="td" sx={cell}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{kindLabel(row)}</Typography>
                          <StatusChip
                            tone={row.kind === 'privacy' ? 'info' : 'neutral'}
                            label={row.kind === 'privacy' ? t('admin.systemV2.requests.kind.privacy') : t('admin.systemV2.requests.partnerSlack')}
                          />
                        </Box>
                      </Box>
                      <Box component="td" sx={cell}>
                        <Typography sx={{ fontSize: 13 }}>{row.who}</Typography>
                        {row.email && <Typography sx={{ fontSize: 12, color: tokens.ink.muted, overflowWrap: 'anywhere' }}>{row.email}</Typography>}
                        {row.note && <Typography sx={{ fontSize: 12, color: tokens.ink.body, mt: 0.5 }}>{row.note}</Typography>}
                      </Box>
                      <Box component="td" sx={{ ...cell, color: tokens.ink.body }}>{row.org}</Box>
                      <Box component="td" sx={{ ...cell, color: tokens.ink.bodyAlt, whiteSpace: 'nowrap', fontSize: 12.5 }}>{shortDate(row.opened)}</Box>
                      <Box component="td" sx={cell}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                          <StatusChip tone={rowTone(row)} label={t(`admin.systemV2.requests.status.${row.status}`, { defaultValue: row.status })} />
                          {due && (
                            <Typography sx={{ fontSize: 11.5, fontWeight: due.urgent ? 600 : 400, color: due.urgent ? tokens.signal.problem.ink : tokens.ink.muted }}>
                              {due.text}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box component="td" sx={{ ...cell, textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {privacyOpen && (row.privacy?.status === 'pending' || row.privacy?.status === 'verified') && (
                            <Button size="small" variant="outlined" onClick={() => statusMutation.mutate({ id: row.privacy!.id, status: 'in_progress' })}>
                              {t('admin.systemV2.requests.start')}
                            </Button>
                          )}
                          {privacyOpen && fulfills && (
                            <Button size="small" variant="outlined" color="error" onClick={() => { setNotes(''); setDialog({ mode: 'deny', request: row.privacy! }); }}>
                              {t('admin.systemV2.requests.deny')}
                            </Button>
                          )}
                          {privacyOpen && fulfills && (
                            <Button size="small" variant="contained" onClick={() => { setNotes(''); setDialog({ mode: 'fulfill', request: row.privacy! }); }}>
                              {t('admin.systemV2.requests.complete')}
                            </Button>
                          )}
                          {privacyOpen && !fulfills && (
                            <Button size="small" variant="contained" onClick={() => statusMutation.mutate({ id: row.privacy!.id, status: 'completed' })}>
                              {t('admin.systemV2.requests.complete')}
                            </Button>
                          )}
                          {row.slack && row.slack.status === 'open' && (
                            <>
                              <Button size="small" onClick={() => slackMutation.mutate({ userId: row.slack!.userId, requestId: row.slack!.requestId, status: 'dismissed' })}>
                                {t('admin.systemV2.requests.dismiss')}
                              </Button>
                              <Button size="small" variant="contained" onClick={() => slackMutation.mutate({ userId: row.slack!.userId, requestId: row.slack!.requestId, status: 'invited' })}>
                                {t('admin.systemV2.requests.markInvited')}
                              </Button>
                            </>
                          )}
                          {row.closedNote && <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{row.closedNote}</Typography>}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </SectionCard>
      {visible.length > 0 && <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{t('admin.systemV2.requests.closedWindow')}</Typography>}
      <FormDialog
        open={Boolean(dialog)}
        title={dialog?.mode === 'deny' ? t('admin.systemV2.requests.deny') : t('admin.systemV2.requests.complete')}
        onClose={() => setDialog(null)}
        onSubmit={() => {
          if (!dialog) return;
          if (dialog.mode === 'deny') statusMutation.mutate({ id: dialog.request.id, status: 'denied', denialReason: notes });
          else fulfillMutation.mutate({ id: dialog.request.id, notes });
        }}
        submitLabel={dialog?.mode === 'deny' ? t('admin.systemV2.requests.deny') : t('admin.systemV2.requests.complete')}
        submitColor={dialog?.mode === 'deny' ? 'error' : 'primary'}
        loading={statusMutation.isPending || fulfillMutation.isPending}
      >
        <Typography sx={{ fontSize: 13.5, mb: 1.5 }}>
          {dialog?.request.type === 'delete' ? t('admin.systemV2.requests.deleteWarning') : t('admin.systemV2.requests.notesHelp')}
        </Typography>
        <TextField
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          label={dialog?.mode === 'deny' ? t('admin.systemV2.requests.denialReason') : t('admin.systemV2.requests.notes')}
          fullWidth
          multiline
          minRows={3}
        />
      </FormDialog>
    </Box>
  );
};

/** Open privacy + open Slack, and whether any privacy request is inside 7 days of its SLA. */
export function useRequestBadge(): { open: number; urgent: boolean; isLoading: boolean } {
  const privacy = useQuery({
    queryKey: ['admin-system-requests-badge'],
    queryFn: async () => {
      const [requests, slack] = await Promise.all([
        systemJson<PrivacyRequest[]>('/admin/privacy-requests?limit=200'),
        systemJson<{ requests: SlackRequest[] }>('/admin/partner-slack/requests?status=open'),
      ]);
      const openPrivacy = requests.filter((request) => OPEN_PRIVACY.has(request.status));
      const urgent = openPrivacy.some((request) => {
        const due = privacyDue(request.requestedAt);
        return due !== null && due.daysLeft <= 7;
      });
      return { open: openPrivacy.length + (slack.requests || []).length, urgent };
    },
  });
  return { open: privacy.data?.open ?? 0, urgent: privacy.data?.urgent ?? false, isLoading: privacy.isLoading };
}
